import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { EffectiveDateSelector } from '@eis/patterns';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';
import { editableRoles, hasPartnerRefusal, partnerRefusalOf } from '../../policies/model/partners';
import {
  coercePayload,
  fieldNameOfIssuePath,
  payloadFormOf,
  payloadSchemaOf,
  payloadValues,
} from '../../policies/model/payload-form';
import { PayloadFields } from '../../policies/ui/PayloadFields';
import {
  useAvailableTarifs,
  useCreateLead,
  useGroupPolicyMemberSchema,
  useLeadSchema,
  usePmmPartnerTypes,
  useUpdateLead,
} from '../api/leads';
import {
  draftRefusalOf,
  incompleteDrafts,
  partnerDraftsOf,
  partnerLinksOf,
  policyRequestOf,
  seedPartnerState,
  type LeadPartnerState,
} from '../model/form';
import { isMemberLead, tarifNameOf } from '../model/leads';
import { refusalMessageOf } from '../model/refusals';
import type { Lead } from '../model/types';
import { LeadPartnerSection } from './LeadPartnerSection';

/**
 * Create and edit (SDD-013 §8.1.5). One form, three sources for what it asks:
 *
 *  - a STANDALONE create picks the tariff from `ListAvailableTarifs`, which answers the schema,
 *    the roles and the lifecycle a lead created TODAY is written against (R-47);
 *  - a MEMBER create is opened from the agreement's Leads tab with the parent preset; schema and
 *    roles come THROUGH THE MASTER (`GetGroupPolicyMemberSchema`, SDD-011 P-8), the policyholder is
 *    inherited and not offered (D7);
 *  - an EDIT is the same form prefilled, against the LEAD's own pin (`GetLeadSchema`, D-L3): a
 *    lead does not switch its tariff or its master, so those are read-only.
 *
 * Every field is GENERATED — payload from the schema, parties from the roles; nothing about a
 * product is written down here. The code is minted by the service.
 */
export type LeadFormMode =
  | { readonly kind: 'create'; readonly parentPolicyCode: string }
  | { readonly kind: 'edit'; readonly lead: Lead };

interface FormSource {
  readonly schema: Record<string, unknown> | undefined;
  readonly rules: readonly InstanceApi.PartnerRoleRule[];
  readonly isPending: boolean;
  readonly isError: boolean;
}

export function LeadFormDialog({
  mode,
  open,
  onClose,
  onSaved,
}: {
  readonly mode: LeadFormMode;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The saved lead's code — the caller navigates to the detail (SDD-002 §5: never back to the list). */
  readonly onSaved: (code: string) => void;
}): ReactNode {
  if (mode.kind === 'edit') {
    return <EditLeadForm lead={mode.lead} open={open} onClose={onClose} onSaved={onSaved} />;
  }
  if (mode.parentPolicyCode !== '') {
    return <MemberLeadForm parentPolicyCode={mode.parentPolicyCode} open={open} onClose={onClose} onSaved={onSaved} />;
  }
  return <StandaloneLeadForm open={open} onClose={onClose} onSaved={onSaved} />;
}

function StandaloneLeadForm({
  open,
  onClose,
  onSaved,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const tarifs = useAvailableTarifs();
  const available = tarifs.data?.tarifs ?? [];
  const [tarifErn, setTarifErn] = useState('');
  const chosen = available.find((tarif) => tarif.ern === tarifErn) ?? available[0];
  const source: FormSource = {
    schema: chosen?.schema,
    rules: chosen?.partnerRoles ?? [],
    isPending: tarifs.isPending,
    isError: tarifs.isError,
  };
  const picker =
    tarifs.isSuccess && available.length === 0 ? (
      <Alert severity="info">{t('leads.create.noTarifs')}</Alert>
    ) : (
      <TextField
        select
        size="small"
        label={t('leads.create.tarif')}
        value={chosen?.ern ?? ''}
        required
        fullWidth
        helperText={t('leads.create.tarifHint')}
        onChange={(event) => {
          setTarifErn(event.target.value);
        }}
      >
        {available.map((tarif) => (
          <MenuItem key={tarif.ern} value={tarif.ern ?? ''}>
            {tarif.name}
          </MenuItem>
        ))}
      </TextField>
    );
  return (
    <LeadFormBody
      formKey={chosen?.ern ?? ''}
      title={t('leads.create.title')}
      description={t('leads.create.description')}
      tarifErn={chosen?.ern ?? ''}
      parentPolicyCode=""
      source={source}
      picker={picker}
      lead={undefined}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function MemberLeadForm({
  parentPolicyCode,
  open,
  onClose,
  onSaved,
}: {
  readonly parentPolicyCode: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const memberSchema = useGroupPolicyMemberSchema(parentPolicyCode);
  // The one tariff a member of the golden agreement may be enrolled in comes from the master's
  // allowed list; the schema names the pinned child version, whose tariff is the one to send.
  const tarifErn = tarifErnOfSchema(memberSchema.data?.schemaErn);
  return (
    <LeadFormBody
      title={t('leads.create.titleMember')}
      description={t('leads.create.descriptionMember')}
      tarifErn={tarifErn}
      parentPolicyCode={parentPolicyCode}
      source={{
        schema: memberSchema.data?.schema,
        rules: editableRoles(memberSchema.data?.partnerRoles, true),
        isPending: memberSchema.isPending,
        isError: memberSchema.isError,
      }}
      picker={
        <TextField size="small" label={t('leads.create.tarif')} value={tarifNameOf(memberSchema.data?.schemaErn)} fullWidth disabled />
      }
      lead={undefined}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function EditLeadForm({
  lead,
  open,
  onClose,
  onSaved,
}: {
  readonly lead: Lead;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const schema = useLeadSchema(lead.code ?? '');
  return (
    <LeadFormBody
      title={t('leads.edit.title')}
      description={t('leads.create.description')}
      tarifErn={tarifErnOfSchema(lead.draft?.configuration?.resolvedCoreSchemaErn)}
      parentPolicyCode={lead.draft?.parentPolicyCode ?? ''}
      source={{
        schema: schema.data?.schema,
        rules: editableRoles(schema.data?.partnerRoles, isMemberLead(lead)),
        isPending: schema.isPending,
        isError: schema.isError,
      }}
      picker={
        <TextField size="small" label={t('leads.create.tarif')} value={tarifNameOf(lead.draft?.configuration?.resolvedTarifVersionErn)} fullWidth disabled />
      }
      lead={lead}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

/** `tarifservice:fahrrad/insurance_core.v1/policy-data` → `tarifservice:fahrrad`. */
function tarifErnOfSchema(schemaErn: string | undefined): string {
  const ern = schemaErn ?? '';
  const slash = ern.indexOf('/');
  return slash === -1 ? ern : ern.slice(0, slash);
}

interface LeadFormBodyProps {
  readonly title: string;
  readonly description: string;
  readonly tarifErn: string;
  readonly parentPolicyCode: string;
  readonly source: FormSource;
  readonly picker: ReactNode;
  readonly lead: Lead | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: (code: string) => void;
  /**
   * Remounts the FIELDS — not the dialog — when the source changes (a different tariff picked):
   * the payload and the parties belong to one schema and one set of roles, and keeping them across
   * a switch would submit one tariff's data under another's. The dialog shell stays mounted so the
   * picker keeps focus and a test's handle to it stays valid.
   */
  readonly formKey?: string;
}

/** The dialog shell: title outside the keyed part, everything stateful inside it. */
function LeadFormBody({ formKey, ...props }: LeadFormBodyProps): ReactNode {
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle>{props.title}</DialogTitle>
      <LeadFormFields key={formKey ?? ''} {...props} />
    </Dialog>
  );
}

function LeadFormFields({
  description,
  tarifErn,
  parentPolicyCode,
  source,
  picker,
  lead,
  onClose,
  onSaved,
}: Omit<LeadFormBodyProps, 'formKey' | 'title' | 'open'>): ReactNode {
  const { t } = useTranslation('insurance');
  const create = useCreateLead();
  const update = useUpdateLead();
  const editing = lead !== undefined;
  const member = parentPolicyCode !== '';
  const rules = source.rules;
  // The Partner service's live types (SDD-014 PS-8): a "Neuer Partner" draft is typed from them
  // and filled in from the chosen type's own fields. Unreachable → the draft falls back to a name.
  const pmmTypes = usePmmPartnerTypes();
  const types = pmmTypes.data?.items ?? [];
  const form = useMemo(() => payloadFormOf(source.schema), [source.schema]);
  const validator = useMemo(() => payloadSchemaOf(form), [form]);

  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(lead?.effectiveFrom ?? null);
  const [validUntil, setValidUntil] = useState<string | null>(lead?.validUntil === undefined || lead.validUntil === '' ? null : lead.validUntil);
  const [values, setValues] = useState<Record<string, string>>(() => payloadValues(lead?.draft?.data));
  const [partners, setPartners] = useState<LeadPartnerState>({ mode: {}, ids: {}, drafts: {} });
  const [seeded, setSeeded] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!seeded && !source.isPending && !pmmTypes.isPending && rules.length > 0) {
    setPartners(seedPartnerState(rules, lead, types));
    setSeeded(true);
  }

  const parsed = validator.safeParse(coercePayload(form, values));
  const fieldErrors: Record<string, string> = {};
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const name = fieldNameOfIssuePath(issue.path);
      if (name !== '' && fieldErrors[name] === undefined) {
        fieldErrors[name] = t(`policies.endorse.errors.${issue.message}` as never, {
          defaultValue: t('policies.endorse.errors.invalid'),
        });
      }
    }
  }
  const dateMissing = effectiveFrom === null || effectiveFrom === '';
  const missingDrafts = incompleteDrafts(rules, partners, types);
  const mutation = editing ? update : create;
  const refusal = partnerRefusalOf(mutation.error, rules, partners.ids);
  const draftRefusals = draftRefusalOf(mutation.error);

  const close = (): void => {
    setSubmitted(false);
    setSeeded(false);
    create.reset();
    update.reset();
    onClose();
  };

  const submit = (): void => {
    setSubmitted(true);
    if (dateMissing || !parsed.success || missingDrafts.length > 0) return;
    const policy = policyRequestOf(tarifErn, parentPolicyCode, effectiveFrom, parsed.data, partnerLinksOf(rules, partners));
    const partnerDrafts = partnerDraftsOf(rules, partners, types);
    if (editing) {
      update.mutate(
        {
          code: lead.code ?? '',
          policy,
          partnerDrafts,
          effectiveFrom,
          ...(validUntil === null ? {} : { validUntil }),
        },
        {
          onSuccess: (response) => {
            onSaved(response.lead?.code ?? lead.code ?? '');
          },
        },
      );
      return;
    }
    create.mutate(
      { policy, partnerDrafts, effectiveFrom, ...(validUntil === null ? {} : { validUntil }) },
      {
        onSuccess: (response) => {
          onSaved(response.lead?.code ?? '');
        },
      },
    );
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          <ConflictNotice conflict={mutation.conflict} />

          {picker}

          <EffectiveDateSelector
            label={t('leads.create.effectiveFrom')}
            value={effectiveFrom}
            required
            helperText={submitted && dateMissing ? t('policies.endorse.errors.required') : t('leads.create.effectiveFromHint')}
            onChange={setEffectiveFrom}
          />
          <EffectiveDateSelector
            label={t('leads.create.validUntil')}
            value={validUntil}
            helperText={t('leads.create.validUntilHint')}
            onChange={setValidUntil}
          />

          {source.isPending ? (
            <Skeleton variant="rounded" height={200} />
          ) : source.isError ? (
            <Alert severity="error">{t('leads.create.schemaError')}</Alert>
          ) : (
            <>
              <LeadPartnerSection
                rules={rules}
                state={partners}
                member={member}
                refusal={refusal}
                draftRefusals={draftRefusals}
                showDraftErrors={submitted}
                types={types}
                typesUnavailable={pmmTypes.isError}
                onChange={setPartners}
              />
              <PayloadFields
                form={form}
                values={values}
                errors={submitted ? fieldErrors : {}}
                onChange={(name, value) => {
                  setValues((current) => ({ ...current, [name]: value }));
                }}
              />
            </>
          )}

          {mutation.isError && !hasPartnerRefusal(refusal) && draftRefusals.length === 0 && mutation.conflict === null && (
            <Alert severity="error" data-testid="lead-form-refusal">
              {refusalMessageOf(mutation.error)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('leads.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || source.isPending || source.isError || tarifErn === ''}
          onClick={submit}
        >
          {editing ? t('leads.edit.submit') : t('leads.create.submit')}
        </Button>
      </DialogActions>
    </>
  );
}
